import env from "app/utils/env";
import axios from "app/utils/http/http";
import {
	CHECK_STOPOVER_AVAILABILITIES,
	FETCH_ALL_STOPOVER_CITIES,
	FETCH_STOPOVER_ACCOMMODATION_DESCRIPTION,
	FETCH_STOPOVER_AVAILABLE_DEPARTURE_DATE_CITIES,
	FETCH_STOPOVER_PACKAGE,
	FETCH_STOPOVER_QUOTE,
	RESTORE_BOOKING_SESSION_STOPOVER,
	SEND_ESTIMATE_STOPOVER,
} from "app/pages/Stopover/stopoverActionTypes";
import {
	BOOK,
	BOOK_EXTERNAL_PAYMENT,
	CLEAR_PROMOTION_VALUE,
	FETCH_FLIGHT_OPTIONS,
	PAYMENT_CALLBACK,
	PRE_BOOK,
	SEND_PROMOTION_CODE,
} from "app/actionTypes";
import { getCredentials } from "app/utils/auth";
import { getStore } from "app/configureStore";
import { getOperatorAuthToken } from "app/pages/Auth/authSelectors";
import qs from "qs";

// @see https://stopover.canary.perfectstay.io/swagger/index.html#/

export const fetchAllStopoverCities = ({ brand, locale }) => {
	const endpoint = env("STOPOVER_API_URL");

	return {
		type: FETCH_ALL_STOPOVER_CITIES,
		promise: axios.get(`${endpoint}/cities/${brand}/${locale}`),
	};
};

export const fetchStopoverAvailableDepartureDateAndCities = ({ uri, shop, productsVisibility }) => {
	const endpoint = env("CONTENT_API_URL") || env("BASE_URL");

	return {
		type: FETCH_STOPOVER_AVAILABLE_DEPARTURE_DATE_CITIES,
		promise: axios.get(`${endpoint}/${productsVisibility}/${shop}/${uri}.json`),
	};
};

let fetchStopoverQuoteController = null;

export const fetchStopoverQuote = quotationPayload => {
	if (fetchStopoverQuoteController) {
		fetchStopoverQuoteController.abort();
	}

	fetchStopoverQuoteController = new AbortController();

	return {
		type: FETCH_STOPOVER_QUOTE,
		promise: axios.post(
			`${env("STOPOVER_API_URL")}/quote`,
			{ ...quotationPayload },
			{
				headers: { "Content-type": "application/json" },
				signal: fetchStopoverQuoteController.signal,
			}
		),
	};
};

export const fetchStopoverFlightOptions = payload => {
	return {
		type: FETCH_FLIGHT_OPTIONS,
		promise: axios.post(`${env("STOPOVER_API_URL")}/flightOptions`, payload, {
			headers: {
				"Content-type": "text/plain",
			},
		}),
	};
};

export const fetchStopoverAccommodationDescription = ({
	bookingFlowId,
	accommodationId,
	stopoverDuration,
}) => {
	return {
		type: FETCH_STOPOVER_ACCOMMODATION_DESCRIPTION,
		promise: axios.post(`${env("STOPOVER_API_URL")}/accommodationDescription`, {
			bookingFlowId,
			accommodationId,
			stopOverDuration: Number(stopoverDuration),
		}),
	};
};

export const checkStopoverAvailibilities = ({
	bookingFlowId,
	accommodationId,
	boardCode,
	price,
	abTests,
	stopoverDuration,
}) => {
	return {
		type: CHECK_STOPOVER_AVAILABILITIES,
		promise: axios.post(`${env("STOPOVER_API_URL")}/accommodationQuote`, {
			bookingFlowId,
			accommodationId,
			boardCode,
			price,
			abTests,
			stopOverDuration: Number(stopoverDuration),
		}),
	};
};

export const fetchStopoverPackage = ({ bookingFlowId, stopOverDuration }) => {
	return {
		type: FETCH_STOPOVER_PACKAGE,
		promise: axios.post(`${env("STOPOVER_API_URL")}/package`, {
			bookingFlowId,
			stopOverDuration,
		}),
	};
};

export const stopoverPreBook = data => {
	return {
		type: PRE_BOOK,
		promise: axios.post(`${env("STOPOVER_API_URL")}/preBook`, data, {
			headers: {
				"Content-type": "text/plain",
			},
		}),
	};
};

export const stopoverBookExternalPayment = ({ bookingFlowId }) => {
	const url = `${env("STOPOVER_API_URL")}/bookExternalPayment`;

	return {
		type: BOOK_EXTERNAL_PAYMENT,
		promise: axios.post(
			url,
			{ bookingFlowId },
			{
				headers: {
					"Content-type": "application/json",
				},
			}
		),
	};
};

export const stopoverPaymentCallback = ({ format, bookingFlowId, adyenStateDataDetails = {} }) => {
	const payload = {
		...(Object.keys(adyenStateDataDetails).length > 0 && {
			adyenAdditionalDetails: JSON.stringify(adyenStateDataDetails),
		}),
	};

	const data = Object.keys(payload).length > 0 ? qs.stringify(payload) : "";

	return {
		type: PAYMENT_CALLBACK,
		promise: axios.post(`${env("STOPOVER_API_URL")}/paymentCallback`, data, {
			params: { format, bookingFlowId },
			headers: {
				"Content-type": "application/x-www-form-urlencoded",
			},
		}),
	};
};

export const stopoverBook = paymentPayload => {
	const url = `${env("STOPOVER_API_URL")}/book`;
	const { token } = getCredentials();
	const headers = {
		Authorization: token,
		"Content-type": "application/json",
	};
	return {
		type: BOOK,
		promise: axios.post(url, paymentPayload, { headers }),
	};
};

export const sendStopoverPromotionCode = promotionCode => {
	return {
		type: SEND_PROMOTION_CODE,
		promise: axios.post(`${env("STOPOVER_API_URL")}/promoCode`, promotionCode, {
			headers: {
				"Content-type": "application/json",
			},
		}),
	};
};

export const clearStopoverPromotionValue = payload => {
	return {
		type: CLEAR_PROMOTION_VALUE,
		promise: axios.post(`${env("STOPOVER_API_URL")}/promoCode`, payload, {
			headers: {
				"Content-type": "application/json",
			},
		}),
	};
};

// l'api du sales tools que tu appelles est protégée.
// Pour y avoir accès il faut utiliser le token cognito que l'agent reçoit quand il se connecte au sales tools.
// Il faut recupérer le paramètre 'agentToken' dans la redirect url .
// Il faudra mettre un header 'Authorization = agentToken' pour appeler l'api du sales tools quand tu fais sendEstimate.
export const sendEstimateStopover = payload => {
	const state = getStore().getState();

	const token = getOperatorAuthToken(state);

	return {
		type: SEND_ESTIMATE_STOPOVER,
		promise: axios.post(`${env("SALES_API_URL")}/stopover/estimate`, payload, {
			headers: {
				Authorization: token,
				"Content-type": "text/plain",
			},
		}),
	};
};

export const restoreBookingSessionStopover = ({ bookingFlowId }) => {
	return {
		type: RESTORE_BOOKING_SESSION_STOPOVER,
		promise: axios.post(
			`${env("STOPOVER_API_URL")}/restoreBookingSession`,
			{ bookingFlowId },
			{
				headers: {
					"Content-type": "text/plain",
				},
			}
		),
	};
};
